import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import { 
  CloudProviderType, 
  CustomerType, 
  EnvironmentType,
  EnvironmentStatus,
  AzureDetails,
  GCPDetails,
  AWSDetails
} from '../../types/environment';

interface Company {
  id: string;
  name: string;
}

interface Environment {
  id: string;
  name: string;
  cloudProviderType: CloudProviderType;
  customerType: CustomerType;
  environmentType: EnvironmentType;
  status: EnvironmentStatus;
  companyId: string;
  azureDetails?: AzureDetails | boolean;
  gcpDetails?: GCPDetails | boolean;
  awsDetails?: AWSDetails | boolean;
}

const EnvironmentEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    cloudProviderType: '' as CloudProviderType | '',
    customerType: 'ExistingCustomer' as CustomerType,
    companyId: '',
    environmentType: 'Dev' as EnvironmentType,
    status: 'Open' as EnvironmentStatus,
  });

  // Cloud provider details state
  const [azureDetails, setAzureDetails] = useState<AzureDetails>({});
  const [gcpDetails, setGCPDetails] = useState<GCPDetails>({});
  const [awsDetails, setAWSDetails] = useState<AWSDetails>({});

  // Fetch environment data
  const { data: environment, isLoading: isLoadingEnv } = useQuery({
    queryKey: ['environment', id],
    queryFn: async () => {
      const response = await api.get(`/environments/${id}`);
      return response.data as Environment;
    },
    enabled: !!id,
  });

  // Fetch companies
  const { data: companiesData } = useQuery({
    queryKey: ['companies'],
    queryFn: async () => {
      const response = await api.get('/companies');
      return response.data;
    },
  });

  // Set form data when environment is loaded
  useEffect(() => {
    if (environment) {
      setFormData({
        name: environment.name,
        cloudProviderType: environment.cloudProviderType,
        customerType: environment.customerType,
        companyId: environment.companyId,
        environmentType: environment.environmentType,
        status: environment.status,
      });

      // Set cloud provider details if they exist
      if (typeof environment.azureDetails === 'object' && environment.azureDetails) {
        setAzureDetails(environment.azureDetails as AzureDetails);
      }
      if (typeof environment.gcpDetails === 'object' && environment.gcpDetails) {
        setGCPDetails(environment.gcpDetails as GCPDetails);
      }
      if (typeof environment.awsDetails === 'object' && environment.awsDetails) {
        setAWSDetails(environment.awsDetails as AWSDetails);
      }
    }
  }, [environment]);

  // Update environment mutation
  const updateMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.patch(`/environments/${id}`, data);
      return response.data;
    },
    onSuccess: (data) => {
      toast.success('Environment updated successfully');
      // Invalidate and refetch the environment data
      queryClient.invalidateQueries({ queryKey: ['environment', id] });
      // Update the cache with the new data immediately
      queryClient.setQueryData(['environment', id], data);
      navigate(`/environments/${id}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update environment');
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.companyId) {
      toast.error('Please fill in all required fields');
      return;
    }

    const updateData: any = { ...formData };
    
    // Add cloud provider details if they exist
    if (formData.cloudProviderType === 'Azure' && Object.keys(azureDetails).length > 0) {
      updateData.azureDetails = azureDetails;
    } else if (formData.cloudProviderType === 'GCP' && Object.keys(gcpDetails).length > 0) {
      updateData.gcpDetails = gcpDetails;
    } else if (formData.cloudProviderType === 'AWS' && Object.keys(awsDetails).length > 0) {
      updateData.awsDetails = awsDetails;
    }

    await updateMutation.mutateAsync(updateData);
  };

  const renderProviderFields = () => {
    switch (formData.cloudProviderType) {
      case 'Azure':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Azure Configuration</h3>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="azure-app-id" className="block text-sm font-medium text-gray-700">
                  Application ID
                </label>
                <input
                  type="text"
                  id="azure-app-id"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={azureDetails.applicationId || ''}
                  onChange={(e) => setAzureDetails({ ...azureDetails, applicationId: e.target.value })}
                />
              </div>

              <div>
                <label htmlFor="azure-subscription-id" className="block text-sm font-medium text-gray-700">
                  Subscription ID
                </label>
                <input
                  type="text"
                  id="azure-subscription-id"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={azureDetails.subscriptionId || ''}
                  onChange={(e) => setAzureDetails({ ...azureDetails, subscriptionId: e.target.value })}
                />
              </div>

              <div>
                <label htmlFor="azure-tenant-id" className="block text-sm font-medium text-gray-700">
                  Tenant ID
                </label>
                <input
                  type="text"
                  id="azure-tenant-id"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={azureDetails.tenantId || ''}
                  onChange={(e) => setAzureDetails({ ...azureDetails, tenantId: e.target.value })}
                />
              </div>

              <div>
                <label htmlFor="azure-client-secret" className="block text-sm font-medium text-gray-700">
                  Client Secret
                </label>
                <input
                  type="password"
                  id="azure-client-secret"
                  placeholder="Enter new secret to update"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={azureDetails.clientSecret || ''}
                  onChange={(e) => setAzureDetails({ ...azureDetails, clientSecret: e.target.value })}
                />
              </div>

              <div>
                <label htmlFor="azure-subscription-name" className="block text-sm font-medium text-gray-700">
                  Subscription Name
                </label>
                <input
                  type="text"
                  id="azure-subscription-name"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={azureDetails.subscriptionName || ''}
                  onChange={(e) => setAzureDetails({ ...azureDetails, subscriptionName: e.target.value })}
                />
              </div>

              <div>
                <label htmlFor="azure-tenant-name" className="block text-sm font-medium text-gray-700">
                  Tenant Name
                </label>
                <input
                  type="text"
                  id="azure-tenant-name"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={azureDetails.tenantName || ''}
                  onChange={(e) => setAzureDetails({ ...azureDetails, tenantName: e.target.value })}
                />
              </div>

              <div className="sm:col-span-2">
                <div className="flex items-center">
                  <input
                    id="azure-csp"
                    type="checkbox"
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    checked={azureDetails.cspSubscription || false}
                    onChange={(e) => setAzureDetails({ ...azureDetails, cspSubscription: e.target.checked })}
                  />
                  <label htmlFor="azure-csp" className="ml-2 block text-sm text-gray-900">
                    CSP Subscription
                  </label>
                </div>
              </div>
            </div>
          </div>
        );

      case 'GCP':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">Google Cloud Platform Configuration</h3>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="gcp-project" className="block text-sm font-medium text-gray-700">
                  Project Name
                </label>
                <input
                  type="text"
                  id="gcp-project"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={gcpDetails.projectName || ''}
                  onChange={(e) => setGCPDetails({ ...gcpDetails, projectName: e.target.value })}
                />
              </div>

              <div>
                <label htmlFor="gcp-service-account" className="block text-sm font-medium text-gray-700">
                  Service Account
                </label>
                <input
                  type="text"
                  id="gcp-service-account"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={gcpDetails.serviceAccount || ''}
                  onChange={(e) => setGCPDetails({ ...gcpDetails, serviceAccount: e.target.value })}
                />
              </div>

              <div>
                <label htmlFor="gcp-client-id" className="block text-sm font-medium text-gray-700">
                  Client ID
                </label>
                <input
                  type="text"
                  id="gcp-client-id"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={gcpDetails.clientId || ''}
                  onChange={(e) => setGCPDetails({ ...gcpDetails, clientId: e.target.value })}
                />
              </div>

              <div>
                <label htmlFor="gcp-client-secret" className="block text-sm font-medium text-gray-700">
                  Client Secret
                </label>
                <input
                  type="password"
                  id="gcp-client-secret"
                  placeholder="Enter new secret to update"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={gcpDetails.clientSecret || ''}
                  onChange={(e) => setGCPDetails({ ...gcpDetails, clientSecret: e.target.value })}
                />
              </div>

              <div className="sm:col-span-2">
                <label htmlFor="gcp-jwt" className="block text-sm font-medium text-gray-700">
                  JWT Provider
                </label>
                <textarea
                  id="gcp-jwt"
                  rows={3}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={gcpDetails.jwtProvider || ''}
                  onChange={(e) => setGCPDetails({ ...gcpDetails, jwtProvider: e.target.value })}
                />
              </div>
            </div>
          </div>
        );

      case 'AWS':
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">AWS Configuration</h3>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="aws-client-id" className="block text-sm font-medium text-gray-700">
                  Client ID / Access Key ID
                </label>
                <input
                  type="text"
                  id="aws-client-id"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={awsDetails.clientId || ''}
                  onChange={(e) => setAWSDetails({ ...awsDetails, clientId: e.target.value })}
                />
              </div>

              <div>
                <label htmlFor="aws-secret-key" className="block text-sm font-medium text-gray-700">
                  Client Secret Key / Secret Access Key
                </label>
                <input
                  type="password"
                  id="aws-secret-key"
                  placeholder="Enter new secret to update"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={awsDetails.clientSecretKey || ''}
                  onChange={(e) => setAWSDetails({ ...awsDetails, clientSecretKey: e.target.value })}
                />
              </div>
            </div>
          </div>
        );

      case 'Rackspace':
        return (
          <div className="p-4 bg-blue-50 rounded-md">
            <p className="text-sm text-blue-800">
              Rackspace configuration is handled through their portal.
            </p>
          </div>
        );

      case 'OnPremise':
        return (
          <div className="p-4 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-600">
              On-Premise environments don't require cloud credentials.
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  if (isLoadingEnv) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading environment...</div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8">
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Edit Environment
          </h2>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="mt-8 space-y-8">
        {/* Basic Information */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              Basic Information
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Update the basic details for the environment
            </p>
          </div>
          <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                  Environment Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <select
                  id="status"
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as EnvironmentStatus })}
                >
                  <option value="Open">Open</option>
                  <option value="Provisioning">Provisioning</option>
                  <option value="Provisioned">Provisioned</option>
                  <option value="Offboarding">Offboarding</option>
                  <option value="Terminated">Terminated</option>
                </select>
              </div>

              <div>
                <label htmlFor="customer-type" className="block text-sm font-medium text-gray-700">
                  Customer Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="customer-type"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={formData.customerType}
                  onChange={(e) => setFormData({ ...formData, customerType: e.target.value as CustomerType })}
                >
                  <option value="ExistingCustomer">Existing Customer</option>
                  <option value="NewOnboarding">New Onboarding</option>
                </select>
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700">
                  Company <span className="text-red-500">*</span>
                </label>
                <select
                  id="company"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={formData.companyId}
                  onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                >
                  <option value="">Select a company</option>
                  {companiesData?.data?.map((company: Company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="env-type" className="block text-sm font-medium text-gray-700">
                  Environment Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="env-type"
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                  value={formData.environmentType}
                  onChange={(e) => setFormData({ ...formData, environmentType: e.target.value as EnvironmentType })}
                >
                  <option value="Dev">Development</option>
                  <option value="Stage">Staging</option>
                  <option value="Prod">Production</option>
                  <option value="UAT">UAT</option>
                </select>
              </div>

              <div>
                <label htmlFor="provider" className="block text-sm font-medium text-gray-700">
                  Cloud Provider Type
                </label>
                <input
                  type="text"
                  id="provider"
                  disabled
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm bg-gray-100 sm:text-sm"
                  value={formData.cloudProviderType}
                />
                <p className="mt-1 text-xs text-gray-500">Cloud provider type cannot be changed after creation</p>
              </div>
            </div>
          </div>
        </div>

        {/* Cloud Provider Configuration */}
        {formData.cloudProviderType && (
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="px-4 py-5 sm:px-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Cloud Provider Configuration
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Update the credentials and settings for the cloud provider
              </p>
            </div>
            <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
              {renderProviderFields()}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate(`/environments/${id}`)}
            className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Cancel
          </button>
          
          <button
            type="submit"
            disabled={updateMutation.isPending}
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EnvironmentEdit;